import React, { useRef, useLayoutEffect, useState, useMemo } from "react";
import { Clock, BookOpen, Bell, Flame, CloudSun, Timer, ScrollText } from "lucide-react";
import { useZmanimData } from '../useZmanimData';

const MIN_PIXELS_PER_MINUTE = 0.3;

const C = {
  bg:          "#f5ead4",
  bgLight:     "#faf3e0",
  bgHeader:    "#6b3a1f",
  bgCard:      "#eedfc0",
  border:      "#c9a96e",
  borderDark:  "#9b7a3d",
  text:        "#3d1f0a",
  textMuted:   "#8a6040",
  gold:        "#9b7a3d",
  goldLight:   "#c4973d",
  emergency:   "#b03a2e",
  axisLine:    "#c9a96e",
};

const FlexibleSpacer = ({ minutes }) => (
  <div style={{ flexGrow: minutes, minHeight: `${Math.max(0, minutes * MIN_PIXELS_PER_MINUTE)}px`, flexShrink: 0, flexBasis: '0px' }} />
);

const SimpleItem = ({ item, isCurrentTime, isPast }) => {
  const IconComponent = item.icon;
  return (
    <div style={{ direction: 'rtl', opacity: isPast ? 0.6 : 1, padding: '7px 0', display: 'flex', gap: '12px', alignItems: 'center', flexShrink: 0 }}>
      <div style={{ width: '44px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
        <div style={{
          backgroundColor: C.bgLight,
          borderRadius: '50%',
          padding: '8px',
          border: isCurrentTime ? `2px solid ${C.emergency}` : `1.5px solid ${C.border}`,
          boxShadow: isCurrentTime ? `0 0 6px ${C.emergency}40` : `inset 0 1px 2px rgba(0,0,0,0.08)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <IconComponent style={{ color: isCurrentTime ? C.emergency : (isPast ? C.textMuted : C.gold), width: '40px', height: '40px' }} />
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 12px 0 48px', borderBottom: `1px dashed ${C.border}50`, gap: '8px', minWidth: 0 }}>
        <span style={{ color: isCurrentTime ? C.emergency : (isPast ? C.textMuted : C.text), fontSize: '1em', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{item.title}</span>
        {item.subtitle && <span style={{ color: C.textMuted, fontSize: '0.8em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.subtitle}</span>}
        <span style={{ color: isCurrentTime ? C.emergency : C.gold, fontSize: '1em', fontWeight: 700, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{item.time}</span>
      </div>
    </div>
  );
};

const AnnotationItem = ({ item, isPast }) => {
  const pills = item.items || [{ value: item.title }];
  return (
    <div style={{ direction: 'rtl', opacity: isPast ? 0.6 : 1, display: 'flex', gap: '12px', alignItems: 'flex-start', flexShrink: 0 }}>
      <div style={{ width: '44px', flexShrink: 0, display: 'flex', justifyContent: 'center', paddingTop: '6px' }}>
        <div style={{
          width: '10px', height: '10px', borderRadius: '50%',
          backgroundColor: C.gold,
          border: `2px solid ${C.bgLight}`,
          boxShadow: `inset 0 1px 2px rgba(0,0,0,0.1)`,
          zIndex: 10,
        }} />
      </div>
      <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '4px', padding: '2px 0' }}>
        {pills.map((pill, idx) => (
          <span key={idx} style={{
            display: 'inline-flex', alignItems: 'center',
            backgroundColor: C.bgLight,
            border: `1px solid ${C.border}60`,
            borderRadius: '6px',
            padding: '1px 8px',
            fontSize: '0.72em', fontWeight: 500, color: C.text,
            lineHeight: 1.3, maxWidth: '100%',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {pill.value || pill.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const HighlightedItem = ({ item, isCurrentTime, isPast }) => {
  const IconComponent = item.icon;
  const accentColor = item.accentColor || C.gold;
  return (
    <div style={{ direction: 'rtl', opacity: isPast ? 0.6 : 1, padding: '8px 0', display: 'flex', gap: '12px', flexShrink: 0 }}>
      <div style={{ width: '44px', flexShrink: 0, display: 'flex', justifyContent: 'center', paddingTop: '10px' }}>
        <div style={{
          width: '56px', height: '56px', borderRadius: '10px',
          backgroundColor: C.bgLight,
          border: isCurrentTime ? `2px solid ${C.emergency}` : `1.5px solid ${C.border}`,
          boxShadow: `inset 0 1px 3px rgba(0,0,0,0.1)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: isCurrentTime ? C.emergency : accentColor,
        }}>
          <IconComponent size={28} />
        </div>
      </div>
      <div style={{ flex: 1, marginLeft: '40px', marginRight: '40px' }}>
        <div style={{
          backgroundColor: C.bgCard,
          border: isCurrentTime ? `2px solid ${C.emergency}` : `1.5px solid ${C.border}`,
          borderRadius: '10px',
          padding: '12px 22px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(107,58,31,0.12)',
        }}>
          <div style={{ position: 'absolute', top: 0, right: 0, width: '5px', height: '100%', backgroundColor: isCurrentTime ? C.emergency : accentColor }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: item.subtitle ? '4px' : 0, gap: '8px', minWidth: 0 }}>
            <span style={{ color: C.text, fontSize: '1.1em', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{item.title}</span>
            <span style={{
              backgroundColor: C.bgLight, color: C.gold,
              border: `1px solid ${C.border}`,
              borderRadius: '8px', padding: '2px 16px',
              fontSize: '1em', fontWeight: 700, flexShrink: 0,
            }}>{item.time}</span>
          </div>
          {item.subtitle && <span style={{ color: C.textMuted, fontSize: '0.8em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{item.subtitle}</span>}
        </div>
      </div>
    </div>
  );
};

const NowIndicator = ({ currentTimeStr }) => (
  <div style={{ direction: 'rtl', display: 'flex', alignItems: 'center', flexShrink: 0, position: 'relative' }}>
    <div style={{ position: 'absolute', borderTop: `2px solid ${C.gold}`, right: '28px', left: 0, top: '50%', transform: 'translateY(-1px)', zIndex: 0 }} />
    <div style={{ width: '44px', flexShrink: 0, zIndex: 1 }} />
    <div style={{ flex: 1, display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 2 }}>
      <span style={{
        backgroundColor: C.gold,
        color: C.bgLight,
        fontWeight: 900,
        padding: '4px 24px',
        borderRadius: '6px',
        fontSize: '1.8em',
        lineHeight: '1.2',
        minWidth: '180px',
        textAlign: 'center',
        display: 'inline-block',
        boxShadow: `0 2px 8px rgba(107,58,31,0.3)`,
        border: `2px solid ${C.borderDark}`,
        letterSpacing: '0.5px',
      }}>{currentTimeStr}</span>
    </div>
  </div>
);

const Parchment = ({ zmanimMap = {}, customScheduleItems = [], title = 'סדר היום', showTitle = true, hebrewDate = null, inyaneiItems = null, inyaneiDisplayMode = null }) => {
  const [layoutState, setLayoutState] = useState({ scale: 1, heightPercent: 100 });
  const containerRef = useRef(null);
  const innerListRef = useRef(null);

  const parchmentIconMap = useMemo(() => ({
    misheyakir:               BookOpen,
    misheyakirMachmir:        BookOpen,
    sofZmanShmaMGA:           BookOpen,
    sofZmanShmaMGA16Point1:   BookOpen,
    sofZmanShma:              BookOpen,
    sofZmanTfillaMGA:         ScrollText,
    sofZmanTfillaMGA16Point1: ScrollText,
    sofZmanTfilla:            ScrollText,
    minchaGedola:             Bell,
    minchaGedolaMGA:          Bell,
    minchaKetana:             Bell,
    minchaKetanaMGA:          Bell,
    plagHaMincha:             Timer,
    sofZmanAchilatChametz:    Flame,
    sofZmanBiurChametz:       Flame,
    sofZmanAchilatChametzMGA: Flame,
    sofZmanBiurChametzMGA:    Flame,
    beinHaShmashos:           CloudSun,
  }), []);

  const { processedItems, nowItemIndex, currentTimeStr, currentItemMatch, currentTotalMinutes, dayWithHebrewDate } = useZmanimData({
    zmanimMap, customScheduleItems, hebrewDate,
    prayerColor: C.goldLight,
    lessonColor: C.gold,
    iconMap: parchmentIconMap,
  });

  useLayoutEffect(() => {
    const calculateLayout = () => {
      if (!containerRef.current || !innerListRef.current) return;
      const containerHeight = containerRef.current.clientHeight;
      const el = innerListRef.current;
      el.style.width = '100%';
      el.style.height = 'auto';
      const contentHeight = el.scrollHeight;
      let newScale = 1;
      let newHeightPercent = 100;
      if (contentHeight > containerHeight) {
        newScale = containerHeight / contentHeight;
        el.style.width = `${100 / newScale}%`;
        const newContentHeight = el.scrollHeight;
        if (newContentHeight < contentHeight) newScale = Math.min(1, containerHeight / newContentHeight);
        newHeightPercent = 100 / newScale;
      }
      setLayoutState(prev => {
        if (Math.abs(prev.scale - newScale) < 0.001 && Math.abs(prev.heightPercent - newHeightPercent) < 0.1) {
          el.style.width = `${100 / prev.scale}%`;
          return prev;
        }
        return { scale: newScale, heightPercent: newHeightPercent };
      });
      el.style.width = `${100 / newScale}%`;
    };
    calculateLayout();
    const observer = new ResizeObserver(() => window.requestAnimationFrame(calculateLayout));
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [processedItems]);

  const showInyaneiSidebar = (inyaneiDisplayMode === 'sidebar' || inyaneiDisplayMode === 'combined') && inyaneiItems?.length > 0;
  const showInyaneiSection = inyaneiDisplayMode === 'section' && inyaneiItems?.length > 0;

  const gridRows = [];
  if (showTitle) gridRows.push('auto');
  if (showInyaneiSection) gridRows.push('auto');
  gridRows.push('1fr');

  return (
    <div style={{
      direction: 'rtl', width: '100%', height: '100%',
      backgroundColor: C.bg,
      overflow: 'hidden',
      display: 'grid',
      gridTemplateRows: gridRows.join(' '),
      gridTemplateColumns: showInyaneiSidebar ? '18% 1fr' : '1fr',
      fontSize: 'inherit',
    }}>

      {/* Header */}
      {showTitle && (
        <div style={{
          gridColumn: '1 / -1',
          backgroundColor: C.bgHeader,
          borderBottom: `3px solid ${C.goldLight}`,
          padding: '0.75rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: C.bgLight, fontWeight: 700, fontSize: '20px' }}>
            <Clock size={20} />
            <span>{title}</span>
          </div>
          <span style={{
            backgroundColor: `${C.bgLight}20`,
            border: `1px solid ${C.goldLight}80`,
            color: C.bgLight,
            borderRadius: '6px',
            padding: '4px 20px',
            fontSize: '18px',
            fontWeight: 600,
            minWidth: '180px',
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}>{dayWithHebrewDate}</span>
        </div>
      )}

      {/* Inyanei Section */}
      {showInyaneiSection && (
        <div style={{
          gridColumn: '1 / -1',
          borderBottom: `2px solid ${C.border}60`,
          backgroundColor: C.bgCard,
          padding: '6px 1.5rem',
          display: 'flex', flexWrap: 'wrap', gap: '5px',
          alignItems: 'center', direction: 'rtl',
        }}>
          {inyaneiItems.map((item, idx) => {
            const Icon = item.IconComponent;
            return (
              <span key={idx} style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                backgroundColor: C.bgLight,
                border: `1px solid ${C.border}`,
                borderRadius: '6px', padding: '2px 10px',
                fontSize: '0.72rem', fontWeight: 600, color: C.text,
                whiteSpace: 'nowrap',
              }}>
                <Icon size={12} style={{ color: C.gold, flexShrink: 0 }} />
                {item.value || item.label}
              </span>
            );
          })}
        </div>
      )}

      {/* Inyanei Sidebar */}
      {showInyaneiSidebar && (
        <div style={{
          overflowY: 'auto', padding: '0.75rem',
          backgroundColor: C.bgCard,
          borderRight: `2px solid ${C.border}60`,
          direction: 'rtl',
          display: 'flex', flexDirection: 'column', gap: '5px',
        }}>
          <div style={{ fontWeight: 700, color: C.text, fontSize: '0.8rem' }}>ענייני היום</div>
          {inyaneiItems.map((item, idx) => {
            const Icon = item.IconComponent;
            const isHigh = item.priority === 'high';
            return (
              <div key={`iny-${idx}`} style={{
                display: 'flex', alignItems: 'flex-start', gap: '5px',
                backgroundColor: C.bgLight,
                border: `1px solid ${isHigh ? `${C.gold}80` : C.border}`,
                borderRadius: '6px', padding: '4px 8px',
              }}>
                <Icon size={13} style={{ color: isHigh ? '#b45309' : C.gold, flexShrink: 0, marginTop: '2px' }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 600, color: C.text, lineHeight: 1.3, wordBreak: 'break-word' }}>
                    {item.value || item.label}
                  </div>
                  {item.detail && (
                    <div style={{ fontSize: '0.6rem', color: C.textMuted, lineHeight: 1.2, marginTop: '1px' }}>
                      {item.detail}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Main Timeline */}
      <div ref={containerRef} style={{ backgroundColor: C.bg, position: 'relative', overflow: 'hidden', height: '100%' }}>
        <div
          ref={innerListRef}
          style={{
            width: `${100 / layoutState.scale}%`,
            height: `${layoutState.heightPercent}%`,
            transform: `scale(${layoutState.scale})`,
            transformOrigin: 'top right',
            display: 'flex', flexDirection: 'column', minHeight: '100%',
            padding: '1.5rem', boxSizing: 'border-box',
          }}
        >
          <div style={{ position: 'absolute', top: 0, bottom: 0, width: '2px', right: '55px', backgroundColor: C.axisLine, zIndex: 0, opacity: 0.5 }} />

          {processedItems.map((item, index) => {
            const isCurrentTime = currentItemMatch?.index === item.index;
            const isNowIndicatorHere = index === nowItemIndex && !currentItemMatch;
            const isPast = item.realTime < currentTotalMinutes;
            const prevItemRealTime = index === 0 ? item.realTime : processedItems[index - 1].realTime;
            let diff = 0, diffBefore = 0, diffAfter = 0;
            if (isNowIndicatorHere) {
              diffBefore = Math.max(0, currentTotalMinutes - prevItemRealTime);
              diffAfter = Math.max(0, item.realTime - currentTotalMinutes);
            } else {
              diff = index === 0 ? 0 : Math.max(0, item.realTime - prevItemRealTime);
            }
            return (
              <div key={item.index} style={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                {isNowIndicatorHere && (
                  <>
                    <FlexibleSpacer minutes={diffBefore} />
                    <NowIndicator currentTimeStr={currentTimeStr} />
                    <FlexibleSpacer minutes={diffAfter} />
                  </>
                )}
                {!isNowIndicatorHere && <FlexibleSpacer minutes={diff} />}
                {item.type === 'annotation'
                  ? <AnnotationItem item={item} isPast={isPast} />
                  : item.type === 'simple'
                    ? <SimpleItem item={item} isCurrentTime={isCurrentTime} isPast={isPast} />
                    : <HighlightedItem item={item} isCurrentTime={isCurrentTime} isPast={isPast} />}
              </div>
            );
          })}

          {nowItemIndex === processedItems.length && !currentItemMatch && (
            <>
              <div style={{ flexGrow: 1, minHeight: '20px' }} />
              <NowIndicator currentTimeStr={currentTimeStr} />
            </>
          )}
          <div style={{ minHeight: '5px', flexShrink: 0 }} />
        </div>
      </div>
    </div>
  );
};

export default Parchment;
